/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import RetailerModel from "../components/Retailers/RetailerModel";
import {
  setInboxMicroservicePlatforms,
  setInboxMicroserviceMigratingPlatforms
} from '../services/inbox/inboxMsChatPlatform';
import { refreshMergedPlatformCounts } from '../actions/inbox/conversationActions';

/**
 * A custom React hook that synchronizes retailer information with the server using WebSocket.
 * It listens for updates on the retailer's information and triggers a Redux action
 * to fetch the latest data when the information is updated or when the tab becomes visible.
 *
 * @returns {function} Returns the fetchRetailerInfo function. Can be called once the app is initialized.
 *
 * @example
 * // Usage in a component
 * const SomeComponent = () => {
 *   const fetchRetailerInfo = useSyncRetailerInfo();
 *
 *   useEffect(() => {
 *     // Fetch retailer info only once when the component mounts
 *     fetchRetailerInfo();
 *   }, [fetchRetailerInfo]);
 *
 *   return <div>Your component content</div>;
 * }
 */
const useSyncRetailerInfo = () => {
  const dispatch = useDispatch();

  // Don't execute this hook if we're on the login page
  const isLoginPage = window.location.pathname === '/login';

  /**
   * Fetch and dispatch retailer information.
   * This function retrieves the latest retailer info from the server
   * and dispatches an action to update the Redux store.
   *
   * @returns {void} This function does not return any value.
   */
  const fetchRetailerInfo = () => {
    if (isLoginPage) return;

    RetailerModel.getInfo().then((data) => {
      setInboxMicroservicePlatforms(data?.current_retailer?.inbox_microservice_platforms);
      setInboxMicroserviceMigratingPlatforms(data?.current_retailer?.inbox_microservice_migrating_platforms);
      dispatch({ type: 'GET_RETAILER_INFO', data });
      dispatch(refreshMergedPlatformCounts());
    }).catch((error) => {
      // Solo loggear errores que no sean de autenticación (401, 403)
      // para evitar ruido en la consola cuando el usuario no está logueado
      if (error?.status && ![401, 403].includes(error.status)) {
        console.error('Error fetching retailer info:', error);
      }
    });
  };

  useEffect(() => {
    // Skip the entire effect if we're on login page
    if (isLoginPage) return undefined;

    let isComponentMounted = true;

    /**
     * Callback function that handles the received status updates from the WebSocket.
     * Dispatches the action to get retailer information when the status is true.
     *
     * @param {Object} data - The data received from the WebSocket.
     * @param {boolean} data.status - The status of the update.
     */
    const statusListener = (data) => {
      if (data.status === true && isComponentMounted) {
        fetchRetailerInfo();
      }
    };

    // Use the existing socket
    if (socket) {
      socket.on('update_retailer_info', statusListener);
    }

    return () => {
      isComponentMounted = false;
      if (socket) {
        socket.off('update_retailer_info', statusListener);
      }
    };
  }, []);

  // Return the fetchRetailerInfo function for external use
  return { fetchRetailerInfo };
};

export default useSyncRetailerInfo;
